import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PRODUCT_NAME } from '@cipherops/data-source';
import '../styles/Sidebar.css';

interface NavItem {
  label: string;
  path: string;
}

interface CurrentUser {
  display_name?: string;
  email?: string;
  // 2-role model post 2026-06-29. Legacy values may still appear on rows
  // from before the collapse; the sidebar treats anything not 'firm_admin'
  // as 'consultant' for visibility decisions.
  role?: 'firm_admin' | 'consultant' | string;
}

interface SidebarProps {
  currentPath: string;
  /** Called when user clicks the sign-out button. In web mode this clears
   *  the WorkOS session cookie via /auth/logout and redirects to the
   *  sign-in page. (Historical naming: prop is `onLock` because Phase 1
   *  used a local-keychain "lock" concept; kept for API compat with
   *  MainLayout. Behaviourally it's a sign-out in the SaaS shell.) */
  onLock: () => void;
}

/** Show only first + last initial (max 2 chars), uppercase, no diacritics. */
function initialsOf(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Pretty-print the role enum for the user badge. Under the 2-role model
 *  every non-firm_admin role surfaces as "Consultant" (legacy DB values
 *  fold in here too). */
function roleLabel(role?: string): string {
  if (role === 'firm_admin') return 'Firm Admin';
  if (!role) return '';
  return 'Consultant';
}

export default function Sidebar({ currentPath, onLock }: SidebarProps) {
  // Findings has no standalone nav item — findings are created/managed inside
  // an engagement (open an engagement → its Findings list), and finding
  // templates live in the Library. The former "Findings" tab was removed
  // 2026-05-19 as redundant + confusing.

  // Fetch current user once on mount; failures are silent (badge falls back
  // to neutral defaults). The /auth/me endpoint already exists and returns
  // { user_id, firm_id, email, display_name, role }.
  const [me, setMe] = useState<CurrentUser | null>(null);
  // Firm name shown as the sidebar brand. Sourced from Firm Settings
  // (firm_profile.firm_name = the firms.name column) so a firm sees its own
  // identity, not the product name. Falls back to PRODUCT_NAME until loaded /
  // on failure. Interchangeable across deliverables (cover, emails) with this.
  const [firmName, setFirmName] = useState<string>(PRODUCT_NAME);
  const [firmTagline, setFirmTagline] = useState<string>('Penetration Testing Reports');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/v1/auth/me', { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setMe(data);
      } catch {
        // Swallow — badge will render with placeholder values.
      }
      try {
        const fr = await fetch('/api/v1/firm_profile/get', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' }, body: '{}',
        });
        if (!fr.ok) return;
        const fd = await fr.json();
        const name = fd?.firm_profile?.firm_name;
        const tagline = fd?.firm_profile?.firm_tagline;
        if (!cancelled) {
          if (typeof name === 'string' && name.trim()) setFirmName(name.trim());
          if (typeof tagline === 'string' && tagline.trim()) setFirmTagline(tagline.trim());
        }
      } catch {
        // Swallow — brand falls back to the product name / default tagline.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live-update the sidebar brand when Firm Settings saves, so the firm name
  // and tagline change instantly — no page refresh. FirmSettingsScreen fires
  // this after a successful firm_profile/upsert.
  useEffect(() => {
    const onUpdate = (e: Event) => {
      const d = ((e as CustomEvent).detail ?? {}) as { firm_name?: string; firm_tagline?: string };
      if (typeof d.firm_name === 'string' && d.firm_name.trim()) setFirmName(d.firm_name.trim());
      if (typeof d.firm_tagline === 'string') setFirmTagline(d.firm_tagline.trim() || 'Penetration Testing Reports');
    };
    window.addEventListener('firm-profile:updated', onUpdate as EventListener);
    return () => window.removeEventListener('firm-profile:updated', onUpdate as EventListener);
  }, []);

  // Two groups: Workspace (everyday work) + Admin (firm management).
  //
  // Option C client split (2026-05-21):
  //   - Admin > Clients          (/clients)          — full CRUD, admin only
  //   - Workspace > Client Directory (/client-directory) — read-only
  //                                                       repository view
  //                                                       for everyone
  // Admins don't see the Workspace directory item — they have the full
  // admin Clients tab. Templates moved to Admin earlier; same logic.
  const workspaceItems: NavItem[] = [
    { label: 'Client Directory', path: '/client-directory' },
    { label: 'Engagements',      path: '/engagements' },
    { label: 'My Schedule',      path: '/my-schedule' },
    { label: 'Report Builder',   path: '/report-builder' },
    { label: 'Report Analytics', path: '/report-dispatch' },
    { label: 'Library',          path: '/library' },
  ];

  const adminItems: NavItem[] = [
    { label: 'Clients',       path: '/clients' },
    { label: 'Members',       path: '/members' },
    { label: 'Schedule',      path: '/schedule' },
    { label: 'Sales',         path: '/sales' },
    { label: 'Templates',     path: '/templates' },
    { label: 'Audit Log',     path: '/audit-log' },
    { label: 'Firm Settings', path: '/settings' },
  ];

  // Every authenticated role sees Profile under its own "Account" group at
  // the bottom of the workspace nav. Distinct from the firm-wide Settings
  // tab which is admin-only.
  const accountItems: NavItem[] = [
    { label: 'Profile', path: '/profile' },
  ];

  // 2-role model: every authenticated user sees the full Workspace + Admin
  // sidebar. firm_admin gets full CRUD on both sections; consultant gets
  // full CRUD on Workspace and read-only on Admin (per-screen affordances
  // hide / disable mutation controls for consultants). The historical
  // resource_manager special case (narrow scheduling + sales nav) was
  // dropped when that role folded into consultant.
  const visibleWorkspace = workspaceItems;
  const visibleAdmin = adminItems;

  const renderItem = (item: NavItem) => {
    // An item is active on its exact path or any sub-route — so viewing an
    // engagement's Findings (/engagements/:id/findings) keeps Engagements lit.
    const isActive =
      currentPath === item.path ||
      currentPath.startsWith(item.path + '/');

    return (
      <Link
        key={item.label}
        to={item.path}
        className={`nav-item ${isActive ? 'active' : ''}`}
        title={item.label}
        // a11y 2026-06-30: expose the active page to assistive tech. The
        // `.active` class is purely visual; aria-current="page" is what a
        // screen reader announces as "current page" on the lit nav item.
        aria-current={isActive ? 'page' : undefined}
      >
        <span className="nav-label">{item.label}</span>
      </Link>
    );
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <h1 className="sidebar-title">{firmName}</h1>
        <p className="sidebar-subtitle">{firmTagline}</p>
      </div>

      <nav className="sidebar-nav">
        {visibleWorkspace.length > 0 && (
          <>
            <div className="nav-group-label">Workspace</div>
            {visibleWorkspace.map(renderItem)}
          </>
        )}

        {visibleAdmin.length > 0 && (
          <>
            <div className="nav-group-label">Admin</div>
            {visibleAdmin.map(renderItem)}
          </>
        )}

        <div className="nav-group-label">Account</div>
        {accountItems.map(renderItem)}
      </nav>

      <div className="sidebar-footer">
        <div className="user-badge">
          <div className="user-badge-avatar">{initialsOf(me?.display_name)}</div>
          <div className="user-badge-info">
            <div className="user-badge-name">{me?.display_name ?? 'Loading…'}</div>
            <div className="user-badge-role">{roleLabel(me?.role)}</div>
          </div>
        </div>
        <button className="lock-button" onClick={onLock} title="Sign out — clears your session and returns you to the sign-in page">
          Sign Out
        </button>
      </div>
    </aside>
  );
}
